// Registration API Service - Backend Integration
import { apiGet, apiPost, apiPut } from '../services/apiClient';

// Helper to wrap response in { data: ... } format for backward compatibility with axios-style responses
const wrapResponse = (data) => ({ data });

/**
 * Get departments from backend (No-Auth endpoint)
 * Gateway path: /gateway/registration/api/no-auth/departments
 * Backend returns: { status, message, data: { content: [...], totalElements, ... } }
 * This endpoint does NOT require JWT authentication - tenantId must be provided as query parameter
 */
export const getDepartments = async (tenantId = null) => {
  // Get tenantId from sessionStorage if not provided
  if (!tenantId) {
    const storedTenantId = sessionStorage.getItem('tenantId');
    if (storedTenantId) {
      tenantId = parseInt(storedTenantId, 10);
    }
  }
  
  // If still no tenantId, use default tenantId of 1 (for development/testing)
  // In production, this should always be set during login
  if (!tenantId || isNaN(tenantId)) {
    console.warn('⚠️ No tenantId found, using default tenantId: 1');
    tenantId = 1; // Default fallback
  }
  
  // Use no-auth endpoint with tenantId
  const params = {
    tenantId: tenantId,
    page: 0,
    size: 1000,
    isActive: true
  };
  
  //console.log('🌐 Calling no-auth departments endpoint with params:', params);
  const response = await apiGet('/gateway/registration/api/no-auth/departments', params);
  //console.log('📥 Raw API response:', JSON.stringify(response, null, 2));
  
  // Backend returns: { status: "OK", message: "...", data: { content: [...], totalElements, ... } }
  // apiGet returns it directly (not wrapped), so response = { status, message, data: { content: [...] } }
  // Extract the actual departments array from content
  let departments = [];
  
  // Check direct response structure first (most common case)
  if (response?.data?.content && Array.isArray(response.data.content)) {
    // Response format: { status, message, data: { content: [...] } }
    departments = response.data.content;
    //console.log('✅ Found departments in response.data.content:', departments.length, 'items');
  } else if (response?.data && Array.isArray(response.data)) {
    // Direct array in data: { data: [...] }
    departments = response.data;
    //console.log('✅ Found departments in response.data (array):', departments.length, 'items');
  } else if (response?.data?.data?.content && Array.isArray(response.data.data.content)) {
    // Double-wrapped: { data: { data: { content: [...] } } }
    departments = response.data.data.content;
    //console.log('✅ Found departments in response.data.data.content:', departments.length, 'items');
  } else if (response?.data?.data && Array.isArray(response.data.data)) {
    // Double-wrapped array: { data: { data: [...] } }
    departments = response.data.data;
    //console.log('✅ Found departments in response.data.data (array):', departments.length, 'items');
  } else if (Array.isArray(response)) {
    // Direct array response
    departments = response;
    //console.log('✅ Found departments in response (array):', departments.length, 'items');
  } else {
    console.error('❌ Could not find departments in response. Response structure:', JSON.stringify(response, null, 2));
  }
  
  //console.log('📋 Final extracted departments:', departments.length, 'items');
  if (departments.length > 0) {
    //console.log('📋 Sample department:', departments[0]);
  }
  return wrapResponse(departments);
};

/**
 * Get roles from backend
 * Gateway path: /gateway/registration/api/signup/organization/roles
 * Backend returns: { status, message, data: [...] }
 */
export const getRoles = async () => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/organization/roles');
    // Backend returns { status, message, data: [...] }
    // apiGet wraps it in { data: { status, message, data: [...] } }
    // Extract the actual roles array
    let roles = [];
    
    if (response?.data) {
      // Check for ResponseBean format: { data: { status, message, data: [...] } }
      if (response.data.data && Array.isArray(response.data.data)) {
        roles = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array in data
        roles = response.data;
      }
    } else if (Array.isArray(response)) {
      // Direct array response
      roles = response;
    }
    
    // Filter out SUPER_ADMIN and inactive roles, map to frontend format
    const filteredRoles = roles
      .filter(role => {
        const roleCode = (role.roleCode || role.role_code || '').toUpperCase();
        const isActive = role.isActive !== false && role.is_active !== false;
        return roleCode !== 'SUPER_ADMIN' && isActive;
      })
      .map(role => {
        const roleCode = (role.roleCode || role.role_code || '').toLowerCase();
        const roleName = role.roleName || role.role_name || roleCode;
        
        // Map backend role codes to frontend format
        let frontendValue = roleCode;
        if (roleCode === 'team_lead' || roleCode === 'team-lead') {
          frontendValue = 'team-lead';
        }
        
        return {
          value: frontendValue,
          label: roleName.charAt(0).toUpperCase() + roleName.slice(1).replace(/_/g, ' '),
          roleId: role.roleId || role.role_id,
          roleCode: roleCode,
          isRequesterRole: role.isRequesterRole === true || role.is_requester_role === true,
        };
      });
    
    return wrapResponse(filteredRoles);
  } catch (error) {
    console.error('Error fetching roles:', error);
    // Return default roles as fallback
    return wrapResponse([
      { value: 'admin', label: 'Admin', roleId: null, roleCode: 'admin' },
      { value: 'agent', label: 'Agent', roleId: null, roleCode: 'agent' },
      { value: 'team-lead', label: 'Team Lead', roleId: null, roleCode: 'team_lead' },
      { value: 'customer', label: 'Customer', roleId: null, roleCode: 'customer' }
    ]);
  }
};

/**
 * Register user by Super Admin
 * Gateway path: /gateway/registration/UserSignup/register/super-admin
 */
export const registerBySuperAdmin = async (userData) => {
  const response = await apiPost('/gateway/registration/UserSignup/register/super-admin', userData);
  return wrapResponse(response);
};

/**
 * Register user by Admin
 * Gateway path: /gateway/registration/UserSignup/register/admin
 */
export const registerByAdmin = async (userData) => {
  const response = await apiPost('/gateway/registration/UserSignup/register/admin', userData);
  return wrapResponse(response);
};

/**
 * Check email availability
 * Gateway path: /gateway/registration/UserSignup/check-email
 * Backend returns: { available: true/false, message: "..." }
 */
export const checkEmailAvailability = async (email, tenantId = null) => {
  // tenantId is optional - backend will extract from JWT token if not provided
  const queryParams = tenantId ? `&tenantId=${tenantId}` : '';
  const response = await apiPost(`/gateway/registration/UserSignup/check-email?email=${encodeURIComponent(email)}${queryParams}`, null);
  // Backend returns AvailabilityCheckResponse directly: { available, message }
  // Return unwrapped for direct access to available property
  return response;
};

/**
 * Check email availability via legacy check-username endpoint
 * Gateway path: /gateway/registration/UserSignup/check-username
 */
export const checkUsernameAvailability = async (email, tenantId = null) => {
  // tenantId is optional - backend will extract from JWT token if not provided
  const queryParams = tenantId ? `&tenantId=${tenantId}` : '';
  const response = await apiPost(`/gateway/registration/UserSignup/check-username?email=${encodeURIComponent(email)}${queryParams}`, null);
  return wrapResponse(response);
};

/**
 * Check phone availability (registration: raw digits, tenant-scoped).
 * @param {string} phone - 9–10 digit mobile number as entered (no country code)
 * @param {string} [phoneRegion] - Ignored; kept for API compatibility
 * @param {number|null} [tenantId] - Optional; backend falls back to JWT tenant when omitted
 * @param {number|null} [excludeUserId] - Current user ID when editing
 */
export const checkPhoneAvailability = async (phone, phoneRegion = null, tenantId = null, excludeUserId = null) => {
  const params = new URLSearchParams();
  params.set('phone', phone);
  if (tenantId != null) params.set('tenantId', String(tenantId));
  if (excludeUserId != null) params.set('excludeUserId', String(excludeUserId));
  const response = await apiPost(
    `/gateway/registration/UserSignup/check-phone?${params.toString()}`,
    null
  );
  return response;
};

/**
 * Register new customer (No-Auth endpoint)
 * Gateway path: /gateway/registration/api/no-auth/registration/signup
 * This endpoint does NOT require JWT authentication
 * 
 * @param {Object} userData - Registration data
 * @param {string} userData.email - User email
 * @param {string} userData.username - Username
 * @param {string} userData.defaultPassword - Default password
 * @param {string} userData.fullName - Full name
 * @param {string} [userData.firstName] - First name
 * @param {string} [userData.lastName] - Last name
 * @param {string} [userData.phone] - Phone number
 * @param {string} userData.role - Role (should be "CUSTOMER")
 * @param {number} userData.tenantId - Tenant ID (required)
 * @param {number} [userData.departmentId] - Department ID (optional)
 * @param {number} [userData.teamId] - Team ID (optional)
 * @returns {Promise<Object>} Registration response
 */
export const registerCustomerNoAuth = async (userData) => {
  const response = await apiPost('/gateway/registration/api/no-auth/registration/signup', userData);
  return wrapResponse(response);
};

/**
 * Get Google client id for tenant (No-Auth endpoint)
 * GET /gateway/registration/api/no-auth/oauth-config/google/client-id?tenantId={tenantId}
 */
export const getGoogleClientId = async (tenantId) => {
  const response = await apiGet('/gateway/registration/api/no-auth/oauth-config/google/client-id', { tenantId });
  return wrapResponse(response);
};

/**
 * Google OAuth upsert (No-Auth) - create account after explicit consent
 * POST /gateway/registration/api/no-auth/registration/oauth/google
 */
export const googleOauthUpsert = async (payload) => {
  // payload: { idToken, tenantId, clientId, source? }
  const response = await apiPost('/gateway/registration/api/no-auth/registration/oauth/google', payload);
  return wrapResponse(response);
};

/**
 * Create tenant
 * POST /gateway/registration/api/signup/organization/tenants
 */
export const createTenant = async (tenantData) => {
  try {
    const response = await apiPost('/gateway/registration/api/signup/organization/tenants', tenantData);
    return wrapResponse(response);
  } catch (error) {
    console.error('Error creating tenant:', error);
    throw error;
  }
};

/**
 * Get tenant by ID
 * GET /gateway/registration/api/signup/organization/tenants/{tenantId}
 */
export const getTenantById = async (tenantId) => {
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/tenants/${tenantId}`);
    return wrapResponse(response);
  } catch (error) {
    console.error('Error fetching tenant:', error);
    throw error;
  }
};

/**
 * Get all tenants (paginated)
 * GET /gateway/registration/api/signup/organization/tenants
 */
export const getTenants = async (params = {}) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/organization/tenants', params);
    return wrapResponse(response);
  } catch (error) {
    console.error('Error fetching tenants:', error);
    throw error;
  }
};

/**
 * Update tenant
 * PUT /gateway/registration/api/signup/organization/tenants/{tenantId}
 */
export const updateTenant = async (tenantId, tenantData) => {
  try {
    const response = await apiPut(`/gateway/registration/api/signup/organization/tenants/${tenantId}`, tenantData);
    return wrapResponse(response);
  } catch (error) {
    console.error('Error updating tenant:', error);
    throw error;
  }
};

// Service object for backward compatibility
export const registrationService = {
  getDepartments,
  getRoles,
  registerBySuperAdmin,
  registerByAdmin,
  checkEmailAvailability,
  checkUsernameAvailability,
  checkPhoneAvailability,
  registerCustomerNoAuth,
  getGoogleClientId,
  googleOauthUpsert,
  createTenant,
  getTenantById,
  getTenants,
  updateTenant,
};

export default registrationService;
